import { selectRouterQueryParameters } from '@noths/polaris-client-next-redux';
import { selectFeatureFlags } from '@noths/polaris-client-user-configuration';
import { Link } from '@noths/polaris-dev-ts-types';
import { createSelector } from '@reduxjs/toolkit';
import { ParsedUrlQueryInput } from 'querystring';
import * as R from 'ramda';

import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import { selectSelectedFilterOptionsWithoutSortFilter } from 'src/components/organisms/Filter/modules/selectors/selectors';
import { BrowseProvider } from 'src/constants/featureFlags';
import { FilterOption } from 'src/types';
import { ReduxApplicationState } from '../combinedReducer';

export const selectRequestOptionsAsQueryObject = (
  state: ReduxApplicationState,
): ParsedUrlQueryInput => {
  const {
    filterMenu: { selectedSortOption },
    products: { searchTerm },
  } = state;

  const selectedFilterOptions = selectSelectedFilterOptionsWithoutSortFilter(state);

  const queryObject = selectedFilterOptions.reduce(
    (acc, value) =>
      value.selectedByDefault
        ? acc
        : {
            ...acc,
            [value.filterOptionUid || value.filterUid]: value.filterOptionValue,
          },
    {},
  );

  const { previewing } = selectRouterQueryParameters(state);

  if (previewing) {
    queryObject['previewing'] = true;
  }

  if (selectedSortOption) {
    queryObject['sort'] = selectedSortOption.id;
  }

  if (searchTerm) {
    queryObject['term'] = searchTerm;
  }

  return queryObject;
};

export const selectFilterOptions = (state: ReduxApplicationState, filterUid: FilterUid) =>
  R.compose(
    R.map((option: FilterOption) => ({ href: option.value, title: option.title, rel: '' } as Link)),
    R.propOr([], 'options'),
    R.find(R.propEq('uid', filterUid)),
  )(state.filter.collections.filters);

export const selectSelectedFilterOptions = (state: ReduxApplicationState, filterUid: FilterUid) =>
  state.filter.collections.selectedFilterOptions.find((f) => f.filterUid === filterUid)
    ?.filterOptionValue;

export const selectCurrentPage = (state: ReduxApplicationState) => state.products.currentPage;

export const selectTotalPages = (state: ReduxApplicationState) => state.products.totalPages;

export const selectTotalProducts = (state: ReduxApplicationState) => state.products.totalProducts;

export const selectProductsByPage = (state: ReduxApplicationState) => state.products.items;

export const selectNextPageNumber = ({ products }: ReduxApplicationState) =>
  products.currentPage + 1;

export const selectPreviousPageNumber = ({ products }: ReduxApplicationState) =>
  parseInt(Object.keys(products.items)[0]) - 1;

export const selectPartnerShortcode = ({ products }: ReduxApplicationState) =>
  products.partner?.shortcode ?? '';

export const selectPartnerName = ({ products }: ReduxApplicationState) =>
  products.partner?.name ?? '';

export const selectPartnerMetaTagDescription = ({ products }: ReduxApplicationState) =>
  products.partner?.metaTagDescription ?? '';

export const selectPartnerLogoUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.logo ?? '';

const PARTNER_HOMEPAGE_LINK_INDEX = 0;
const PARTNER_PRODUCTS_LINK_INDEX = 1;

export const selectPartnerHomepageUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_HOMEPAGE_LINK_INDEX]?.href ?? '';

export const selectPartnerHomepageRelativeUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_HOMEPAGE_LINK_INDEX]?.rel ?? '';

export const selectPartnerProductsUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_PRODUCTS_LINK_INDEX]?.href ?? '';

export const selectProductFromCode = createSelector(
  [
    ({ products }: ReduxApplicationState) => products.items,
    (_, productCode: number) => productCode,
  ],
  (products, productCode) =>
    Object.values(products)
      .flat()
      .find(({ code }) => code === productCode),
);

export const selectBrowseProviderFeatureFlag = (state: ReduxApplicationState) => {
  return selectFeatureFlags(state)?.[BrowseProvider.NAME];
};

export const selectShowFeaturedProduct = ({
  products: { featuredProduct },
}: ReduxApplicationState) =>
  !!featuredProduct && featuredProduct.inventoryStatus !== 'out_of_stock';

export const selectProviderAttributionToken = ({ products }: ReduxApplicationState) =>
  products.providerAttributionToken;
